import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

type BaseProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  busy?: boolean;
};

/** Icon-only buttons must carry an accessible name. */
type IconOnlyProps = BaseProps & { iconOnly: true; 'aria-label': string; children?: never };
type LabelledProps = BaseProps & { iconOnly?: false; children: ReactNode };

export type ButtonProps = IconOnlyProps | LabelledProps;

const VARIANT_CLASS: Record<Variant, string> = {
  primary: '',
  secondary: 'gsl-btn-secondary',
  ghost: 'gsl-btn-ghost',
  danger: 'gsl-btn-danger',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const { variant = 'primary', size = 'md', icon, busy = false, className = '', iconOnly, disabled, ...rest } = props;
  const classes = [
    'gsl-btn',
    VARIANT_CLASS[variant],
    size === 'sm' ? 'gsl-btn-sm' : '',
    iconOnly ? 'gsl-btn-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const children = 'children' in props ? props.children : null;
  return (
    <button ref={ref} type="button" className={classes} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {icon ? <Icon name={icon} size={size === 'sm' ? 15 : 17} /> : null}
      {children}
    </button>
  );
});
